import { Fragment } from "react"
import { css, cx } from "@emotion/css"
import { scaleLinear } from "@visx/scale"

import { ProgressBar, ProgressBarProps } from "./ProgressBar"

const styles = {
  wrapper: css`
    overflow: auto;
  `,
  root: css`
    display: grid;
    grid-template-columns: min-content minmax(120px, 1fr) min-content;
    grid-template-areas: "name bar value";
    grid-column-gap: 18px;
    grid-row-gap: 10px;
    grid-auto-rows: 14px;
    align-items: center;
  `,
  cell: css`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1;
    color: var(--black, #000);
  `,
  name: css`
    font-size: 12px;
  `,
  value: css`
    font-size: 11px;
    text-align: right;
  `,
}

export type LeaderboardProps<Datum extends Record<string, any>> = {
  data: Datum[]
  getValue: (datum: Datum) => number
  getName: (datum: Datum) => string
  formatValue?: (x: number) => string
  //
  className?: string
  bar?: Omit<ProgressBarProps, "value">
}

export function Leaderboard<Datum extends Record<string, any>>(
  props: LeaderboardProps<Datum>,
) {
  const { data, getValue, getName, formatValue, className, bar = {} } = props
  const max = Math.max(...data.map(getValue))
  const scale = scaleLinear<number>({
    domain: [0, max],
    range: [0, 100],
  })

  data.sort((a, b) => getValue(b) - getValue(a))

  return (
    <div className={styles.wrapper}>
      <div className={cx(styles.root, className)}>
        {data.map((datum, idx) => {
          const value = getValue(datum)
          return (
            <Fragment key={idx}>
              <div className={cx(styles.cell, styles.name, "name")}>
                {getName(datum)}
              </div>
              <div>
                <ProgressBar value={scale(value)} {...bar} />
              </div>
              <div className={cx(styles.cell, styles.value)}>
                {formatValue ? formatValue(value) : value.toString()}
              </div>
            </Fragment>
          )
        })}
      </div>
    </div>
  )
}
